import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import {
  LayoutDashboard, Newspaper, CalendarDays, MessageSquare, Camera, HelpCircle,
  GraduationCap, MapPin, Users, BarChart3, ScrollText, Settings, LogOut, Menu, X, Bell, ShieldCheck, MessagesSquare,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/helpers";
import api from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { API } from "@/lib/api";
import { Logo } from "@/components/Logo";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const MENU = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/news", label: "Berita", icon: Newspaper },
  { to: "/admin/agendas", label: "Agenda", icon: CalendarDays },
  { to: "/admin/aspirations", label: "Aspirasi Masyarakat", icon: MessageSquare },
  { to: "/admin/media", label: "Media Center", icon: Camera },
  { to: "/admin/faq", label: "FAQ / Tanya Bawaslu", icon: HelpCircle },
  { to: "/admin/chat-logs", label: "Riwayat Chat", icon: MessagesSquare },
  { to: "/admin/education", label: "Edukasi", icon: GraduationCap },
  { to: "/admin/offices", label: "Direktori Bawaslu", icon: MapPin },
  { to: "/admin/users", label: "Pengguna", icon: Users },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/audit-log", label: "Audit Log", icon: ScrollText },
  { to: "/admin/settings", label: "Pengaturan", icon: Settings },
];

function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const markRead = () => api.post("/notifications/read").then(load);
  return (
    <DropdownMenu onOpenChange={(o) => o && markRead()}>
      <DropdownMenuTrigger asChild>
        <button className="relative p-2 rounded-full hover:bg-slate-100 transition-colors" data-testid="notif-bell">
          <Bell className="h-5 w-5 text-slate-600" />
          {data.unread > 0 && (
            <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-brandred text-white text-[10px] font-bold flex items-center justify-center">
              {data.unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>Notifikasi</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {data.items.length === 0 && <div className="px-3 py-4 text-sm text-slate-500">Tidak ada notifikasi</div>}
        {data.items.slice(0, 8).map((n) => (
          <div key={n.id} className="px-3 py-2.5 text-sm text-slate-700 border-b border-slate-50 last:border-0">
            {n.message}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const settings = useSettings();

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Memuat…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const doLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-surface flex">
      <aside
        data-testid="admin-sidebar"
        className={`fixed lg:sticky top-0 z-40 h-screen w-64 bg-navy text-slate-300 flex flex-col transition-transform ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="h-16 flex items-center px-5 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <img src={settings?.logo_url ? `${API}/files/${settings.logo_url}` : "/assets/logo-bawaslu-emblem.png"} alt="Logo Bawaslu" className="h-9 w-auto" />
            <div className="leading-tight">
              <div className="text-white font-extrabold text-sm">HARMONI</div>
              <div className="text-[10px] text-slate-400">Admin Panel</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1 hide-scrollbar">
          {MENU.map((m) => (
            <NavLink
              key={m.to}
              to={m.to}
              end={m.end}
              onClick={() => setOpen(false)}
              data-testid={`admin-nav-${m.to.split("/").pop() || "dashboard"}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-brandred text-white" : "hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <m.icon className="h-4.5 w-4.5 shrink-0" style={{ width: 18, height: 18 }} />
              {m.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button onClick={doLogout} data-testid="logout-btn" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-brandred hover:text-white transition-colors">
            <LogOut className="h-4 w-4" /> Keluar
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 sticky top-0 z-20 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2" onClick={() => setOpen(true)} data-testid="admin-menu-toggle">
              <Menu className="h-5 w-5 text-navy" />
            </button>
            <div className="hidden sm:flex items-center gap-2 text-sm font-semibold text-navy">
              <ShieldCheck className="h-4 w-4 text-brandred" /> Bawaslu Sultra
            </div>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2.5 pl-2" data-testid="admin-user-menu">
                  <div className="h-9 w-9 rounded-full bg-navy text-white flex items-center justify-center font-bold text-sm">
                    {user.name?.[0] || "A"}
                  </div>
                  <div className="hidden sm:block text-left leading-tight">
                    <div className="text-sm font-bold text-navy">{user.name}</div>
                    <div className="text-[11px] text-slate-500">{ROLE_LABELS[user.role] || user.role}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/")}>Lihat Situs Publik</DropdownMenuItem>
                <DropdownMenuItem onClick={doLogout} className="text-brandred">Keluar</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
